import React, { useEffect, useState } from "react";
import {
    Box, Button, Flex, Text, Icon, Input, IconButton, Alert, AlertIcon,
    AlertDescription, InputGroup, InputLeftElement, useColorModeValue,
} from "@chakra-ui/react";
import { Plus, Trash2, Smartphone } from "lucide-react";
import Card from "components/card/Card.js";
import { QuickAlert } from "theme/components/alerts";
import { userController } from "lib/user";
import BeatLoader from "react-spinners/BeatLoader";

function AiAgentTestNumbers() {
    const { setAlert, clearAlert, showAlert } = QuickAlert();
    const { getSingleRecord, saveRecord } = userController();
    const headerBorderColor = useColorModeValue("#E6ECFA", "rgba(135, 140, 189, 0.3)");

    const [loading, setLoading]           = useState(true);
    const [saving, setSaving]             = useState(false);
    const [numbers, setNumbers]           = useState([]);
    const [savedNumbers, setSavedNumbers] = useState([]);
    const [chatEnabled, setChatEnabled]   = useState(true);

    // Update only shows once something has actually changed
    const hasChanges = JSON.stringify(numbers) !== JSON.stringify(savedNumbers);

    useEffect(() => {
        loadTestNumbers();
    }, []);

    const loadTestNumbers = () => {
        setLoading(true);
        getSingleRecord("/user/app-settings/test-numbers")
            .then((res) => {
                setNumbers(res.data.numbers || []);
                setSavedNumbers(res.data.numbers || []);
                setChatEnabled(res.data.whatsapp_chat_enabled);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    const addNumber    = () => setNumbers([...numbers, ""]);
    const removeNumber = (index) => setNumbers(numbers.filter((_, i) => i !== index));

    const updateNumber = (index, value) => {
        setNumbers(numbers.map((number, i) => (i === index ? value : number)));
    };

    const saveTestNumbers = () => {
        setSaving(true);
        clearAlert();
        saveRecord("/user/app-settings/test-numbers", { numbers })
            .then((res) => {
                // Server returns the normalised list, so both sides match and
                // the button hides again
                setNumbers(res.data.numbers || []);
                setSavedNumbers(res.data.numbers || []);

                // Support must stay reachable while test numbers can still hand off
                const existing = JSON.parse(localStorage.getItem("app_settings") || "{}");
                localStorage.setItem("app_settings", JSON.stringify({
                    ...existing,
                    whatsapp_support_visible: chatEnabled || (res.data.numbers || []).length > 0,
                }));

                setAlert("success", res.data.message);
                setSaving(false);
            })
            .catch((error) => {
                setAlert("error", error.response.data.errors);
                setSaving(false);
            });
    };

    return (
        <Box>
            <Card mb="20px" p="0">
                <Flex justify="space-between" align="center" pt="20px" pb="16px" px="24px" borderBottom="1px solid" borderColor={headerBorderColor}>
                    <Text color="secondaryGray.900" fontSize="22px" fontWeight="700" lineHeight="100%">
                        Numeri di Test Assistente AI
                    </Text>
                </Flex>

                <Box px="24px" pt="20px" pb="24px">
                    {showAlert()}

                    {loading ? (
                        <Flex justify="center" py="20px">
                            <BeatLoader size={10} color="#422CFA" />
                        </Flex>
                    ) : (
                        <>
                            {chatEnabled && (
                                <Alert status="info" variant="subtle" mb="20px" borderRadius={8}>
                                    <AlertIcon me={2} />
                                    <AlertDescription fontSize="sm">
                                        La Chat WhatsApp AI è attiva, quindi l'assistente risponde a
                                        tutti gli ospiti. I numeri di test hanno effetto solo quando la chat è disattivata.
                                    </AlertDescription>
                                </Alert>
                            )}

                            {numbers.length === 0 ? (
                                <Flex
                                    justify="center" align="center" py="30px" mb="16px"
                                    border="1px dashed" borderColor={headerBorderColor} borderRadius="12px"
                                >
                                    <Text color="gray.400" fontSize="sm">
                                        Nessun numero di test. Aggiungine uno per continuare a testare quando l'AI è disattivata.
                                    </Text>
                                </Flex>
                            ) : (
                                numbers.map((number, index) => (
                                    <Flex key={index} align="center" gap="10px" mb="12px">
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none" h="100%">
                                                <Icon as={Smartphone} boxSize="18px" color="gray.400" />
                                            </InputLeftElement>
                                            <Input
                                                value={number}
                                                placeholder="+39 333 123 4567"
                                                onChange={(e) => updateNumber(index, e.target.value)}
                                            />
                                        </InputGroup>
                                        <IconButton
                                            aria-label="Rimuovi numero"
                                            icon={<Icon as={Trash2} boxSize="18px" />}
                                            variant="outline"
                                            colorScheme="red"
                                            onClick={() => removeNumber(index)}
                                        />
                                    </Flex>
                                ))
                            )}

                            <Button
                                variant="outline"
                                colorScheme="brand"
                                fontWeight="light"
                                leftIcon={<Icon as={Plus} boxSize="18px" />}
                                _hover={{ bg: "brand.500", color: "white" }}
                                transition="background-color 0.2s linear"
                                onClick={addNumber}
                            >
                                Aggiungi Numero
                            </Button>
                        </>
                    )}
                </Box>

                {!loading && hasChanges && (
                    <Flex align="center" pt="16px" pb="16px" px="24px" borderTop="1px solid" borderColor={headerBorderColor}>
                        <Button
                            variant="brand"
                            minWidth="150px"
                            isLoading={saving}
                            onClick={saveTestNumbers}
                        >
                            Aggiorna
                        </Button>
                    </Flex>
                )}
            </Card>
        </Box>
    );
}

export default AiAgentTestNumbers;
